/* Task-family band derivation and limited-evidence gate.
   The heart of the task-family data layer. */

import type {
  Student,
  Task,
  TaskFamilyBand,
  TaskFamilyDistribution,
  TaskFamilyOutcome,
  TermId,
} from "@/types";
import { getRuntimeConfig } from "@/config/runtime";
import { getTaskResult } from "@/lib/report-data";
import { taskRawDetails } from "@/lib/task-raw-details";

/** 3-way percentile cutoffs vs the same-year reference. Matches the
    global P15/P35 performance-band convention (see score-bands.ts). */
export const TASK_FAMILY_BAND_CUTOFFS = { veryLowMax: 15, lowMax: 35 } as const;

/** Limited-evidence gate thresholds. */
export const LIMITED_EVIDENCE_GATE = {
  minItemsAttempted: 3,
} as const;

/** True when this task family has too little evidence to compare. */
export function isLimitedEvidence(
  itemsAttempted: number,
  _itemsTotal: number,
  _timed: boolean,
): boolean {
  void _itemsTotal;
  void _timed;
  return itemsAttempted < LIMITED_EVIDENCE_GATE.minItemsAttempted;
}

/** Map a same-year-reference percentile to a performance band.
    Boundary semantics match the teacher-facing PR convention:
    PR15 and below → Very Low, PR16–PR35 → Low, PR36+ → On Track. */
export function bandFromPercentile(
  percentile: number,
): Exclude<TaskFamilyBand, "limited" | "not_attempted"> {
  if (percentile <= TASK_FAMILY_BAND_CUTOFFS.veryLowMax) return "very_low";
  if (percentile <= TASK_FAMILY_BAND_CUTOFFS.lowMax) return "low";
  return "on_track";
}

/** Resolve one student's outcome for one task family. The single place
    that fuses the completion flag, the gate, and the band. */
export function resolveTaskFamilyOutcome(
  student: Student,
  term: TermId | string,
  task: Task,
  options: { allowSyntheticTaskTelemetry?: boolean } = {},
): TaskFamilyOutcome {
  const result = getTaskResult(student, term, task.id);
  const allowSyntheticTaskTelemetry = options.allowSyntheticTaskTelemetry ?? getRuntimeConfig().usesMockAdapter;
  const raw =
    result.completed && result.percentile != null
      ? taskRawDetails(student.id, task.id, result.percentile, task.itemsTotal, result, {
          allowSynthetic: allowSyntheticTaskTelemetry,
        })
      : null;
  const rawDetailsAvailable = raw != null;

  const notAttempted = !result.completed || raw?.itemsAttempted === 0;
  const limited =
    !notAttempted &&
    (result.percentile == null ||
      (rawDetailsAvailable && isLimitedEvidence(raw.itemsAttempted, task.itemsTotal, task.timed)));

  const band: TaskFamilyBand = notAttempted
    ? "not_attempted"
    : limited
      ? "limited"
      : bandFromPercentile(result.percentile as number);

  return {
    taskId: task.id,
    band,
    percentile: result.percentile ?? null,
    rawDetailsAvailable,
    itemsAttempted: raw?.itemsAttempted ?? 0,
    itemsCorrect: raw?.itemsCorrect ?? 0,
    itemsTotal: task.itemsTotal,
    accuracyPct: raw?.accuracyPct ?? 0,
    correctPerMin: task.timed ? raw?.correctPerMin ?? null : null,
    timed: task.timed,
  };
}

/** Resolve every task-family outcome for one student, in battery order.
    Shared by the student table and the expanded-row card grid. */
export function resolveStudentOutcomes(
  student: Student,
  battery: Task[],
  term: TermId | string,
): { task: Task; outcome: TaskFamilyOutcome }[] {
  return battery.map((task) => ({
    task,
    outcome: resolveTaskFamilyOutcome(student, term, task),
  }));
}

/** Per-task class distribution across the five bands. */
export function taskFamilyDistribution(
  classStudents: Student[],
  term: TermId | string,
  task: Task,
): TaskFamilyDistribution {
  let veryLow = 0;
  let low = 0;
  let onTrack = 0;
  let limited = 0;
  let notAttempted = 0;
  const members: Record<TaskFamilyBand, { id: string; name: string }[]> = {
    very_low: [],
    low: [],
    on_track: [],
    limited: [],
    not_attempted: [],
  };
  for (const s of classStudents) {
    const o = resolveTaskFamilyOutcome(s, term, task);
    members[o.band].push({ id: s.id, name: s.name });
    if (o.band === "very_low") veryLow++;
    else if (o.band === "low") low++;
    else if (o.band === "on_track") onTrack++;
    else if (o.band === "limited") limited++;
    else notAttempted++;
  }
  return {
    ...task,
    veryLow,
    low,
    onTrack,
    limited,
    notAttempted,
    total: veryLow + low + onTrack + limited + notAttempted,
    members,
  };
}

/** Battery-wide distribution map for ClassPage. */
export function taskFamilyDistributionsForBattery(
  classStudents: Student[],
  battery: Task[],
  term: TermId | string,
): Record<string, TaskFamilyDistribution> {
  const out: Record<string, TaskFamilyDistribution> = {};
  for (const task of battery) {
    out[task.id] = taskFamilyDistribution(classStudents, term, task);
  }
  return out;
}
